import React, { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Input } from "@/components/ui/input"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import {
  Edit,
  FileText,
  CheckCircle,
  AlertCircle,
  Target,
  Activity,
  Plus,
  X,
  Save,
  Pill,
  Clock,
  AlertTriangle,
  ClipboardCopy,
  Check,
} from "lucide-react"
import { format } from "date-fns"
import { ageFromDob, formatDateOnly, formatTimestamp, toDateOnlyString } from "@/lib/datetime"
import { useReactToPrint } from "react-to-print"
import { CarePlanResponse } from "@/lib/types"
import api from "@/lib/api"
import { copyNoteToEhr } from "@/lib/copyToEhr"
import { useToast } from "@/hooks/use-toast"

// Medication and care-gap dates are calendar dates: never shifted by time zone.
const safeFormatDate = (dateStr: string | null | undefined, formatStr: string, fallback = "N/A"): string =>
  formatDateOnly(dateStr, formatStr, fallback)

// Print-specific styles
const printStyles = `
  @media print {
    @page {
      size: letter;
      margin: 0.4in 0.4in 0.2in 0.4in;
    }

    html, body {
      height: initial !important;
      overflow: visible !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      margin: 0 !important;
      padding: 0 !important;
    }

    .no-print {
      display: none !important;
    }

    .print-only {
      display: block !important;
    }

    .print-container {
      padding: 0 !important;
    }
  }

  @media screen {
    .print-only {
      display: none !important;
    }
  }
`

// Types for editable care plan data
interface ManagementGoal {
  condition: string
  goal: string
}

interface SmartGoal {
  goal: string
}

interface Intervention {
  item: string
  owner: string
  target_date: string
}

interface Medication {
  medication_dosage_description: string
  start_date: string | null
  prescription_date: string | null
  end_date: string | null
}

interface CareGap {
  gap: string
  due_date: string | null
  plan: string
}

interface EditableCarePlanData {
  management_goals: ManagementGoal[]
  smart_goals: SmartGoal[]
  interventions_and_referrals: Intervention[]
  medications: Medication[]
  care_gaps: CareGap[]
}

interface PatientCarePlanTabProps {
  patientId: string
  carePlan?: CarePlanResponse | null
  loading?: boolean
  onEdit?: () => void
  onApprove?: () => void
  onReject?: () => void
  canApprove?: boolean
  organizationId?: string
  onSaveSuccess?: () => void
}

export function PatientCarePlanTab({
  patientId,
  carePlan,
  loading = false,
  onEdit,
  onApprove,
  onReject,
  canApprove = false,
  organizationId,
  onSaveSuccess,
}: PatientCarePlanTabProps) {
  const { toast } = useToast()
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editData, setEditData] = useState<EditableCarePlanData>({
    management_goals: [],
    smart_goals: [],
    interventions_and_referrals: [],
    medications: [],
    care_gaps: [],
  })

  // Print ref and handler
  const printRef = useRef<HTMLDivElement>(null)

  // Get patient info for document title
  const planData = carePlan?.care_plan_details?.plan || carePlan?.care_plan_details || {}
  const patientInfo = planData.patient || {}
  const firstName = patientInfo.first_name || "Unknown"
  const lastName = patientInfo.last_name || "Unknown"
  const dob = patientInfo.dob || ""

  // Format DOB safely without timezone conversion
  const formatDobDisplay = (dobStr: string | null | undefined): string => {
    if (!dobStr) return "N/A"
    return formatDateOnly(dobStr, "MM/dd/yyyy", dobStr)
  }

  const formatDobForFilename = (dobStr: string) => {
    return formatDateOnly(dobStr, "yyyy-MM-dd", "Unknown")
  }

  const [copyState, setCopyState] = useState<"idle" | "copying" | "copied">("idle")

  // The note is rendered server-side so the portal and the batch scripts stay
  // one implementation; it is fetched on click rather than with the care plan,
  // since most views of this tab never copy.
  const handleCopyToEhr = async () => {
    if (!carePlan?.id) return
    setCopyState("copying")
    try {
      const note = await api.getCarePlanNote(carePlan.id)
      const outcome = await copyNoteToEhr(note)

      if (outcome === "failed") {
        setCopyState("idle")
        toast({
          title: "Could not reach the clipboard",
          description:
            "Your browser blocked the copy. Check clipboard permissions for this site and try again.",
          variant: "destructive",
        })
        return
      }

      setCopyState("copied")
      setTimeout(() => setCopyState("idle"), 2000)
      toast({
        title: outcome === "formatted" ? "Care plan copied" : "Copied as plain text",
        description:
          outcome === "formatted"
            ? "Paste into the encounter note. Plain-text note fields are covered too."
            : "Formatting could not be copied, but the note still reads correctly.",
      })
    } catch (error) {
      console.error("Failed to copy care plan note:", error)
      setCopyState("idle")
      toast({
        title: "Could not build the note",
        description: "The care plan could not be rendered for the EHR. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `CarePlan--LN-${lastName}--FN-${firstName}--DOB-${formatDobForFilename(dob)}--GEN-${toDateOnlyString()}`,
    pageStyle: printStyles,
  })

  // Initialize edit data from care plan
  useEffect(() => {
    if (carePlan) {
      const planData = carePlan.care_plan_details?.plan || carePlan.care_plan_details || {}
      setEditData({
        management_goals: planData.management_goals || [],
        smart_goals: planData.smart_goals || [],
        interventions_and_referrals: planData.interventions_and_referrals || [],
        medications: planData.medications || [],
        care_gaps: planData.care_gaps || [],
      })
    }
  }, [carePlan])

  const handleStartEditing = () => {
    setIsEditing(true)
  }

  const handleCancelEditing = () => {
    // Reset to original data
    if (carePlan) {
      const planData = carePlan.care_plan_details?.plan || carePlan.care_plan_details || {}
      setEditData({
        management_goals: planData.management_goals || [],
        smart_goals: planData.smart_goals || [],
        interventions_and_referrals: planData.interventions_and_referrals || [],
        medications: planData.medications || [],
        care_gaps: planData.care_gaps || [],
      })
    }
    setIsEditing(false)
  }

  const handleSave = async () => {
    if (!carePlan) {
      toast({
        title: "Error",
        description: "No care plan to update",
        variant: "destructive",
      })
      return
    }

    setIsSaving(true)
    try {
      // Reconstruct the care plan details with the original structure
      const originalDetails = carePlan.care_plan_details || {}
      const hasPlanWrapper = !!carePlan.care_plan_details?.plan

      const updatedDetails = hasPlanWrapper
        ? {
            ...originalDetails,
            plan: {
              ...originalDetails.plan,
              management_goals: editData.management_goals,
              smart_goals: editData.smart_goals,
              interventions_and_referrals: editData.interventions_and_referrals,
              medications: editData.medications,
              care_gaps: editData.care_gaps,
            },
          }
        : {
            ...originalDetails,
            management_goals: editData.management_goals,
            smart_goals: editData.smart_goals,
            interventions_and_referrals: editData.interventions_and_referrals,
            medications: editData.medications,
            care_gaps: editData.care_gaps,
          }

      await api.updateCarePlan(carePlan.id, updatedDetails)

      toast({
        title: "Success",
        description: "Care plan updated successfully",
      })

      setIsEditing(false)
      onSaveSuccess?.()
    } catch (error) {
      console.error("Error saving care plan:", error)
      toast({
        title: "Error",
        description: "Failed to save care plan",
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Management Goals handlers
  const updateManagementGoal = (index: number, field: keyof ManagementGoal, value: string) => {
    setEditData((prev) => ({
      ...prev,
      management_goals: prev.management_goals.map((goal, i) =>
        i === index ? { ...goal, [field]: value } : goal
      ),
    }))
  }

  const addManagementGoal = () => {
    setEditData((prev) => ({
      ...prev,
      management_goals: [{ condition: "", goal: "" }, ...prev.management_goals],
    }))
  }

  const removeManagementGoal = (index: number) => {
    setEditData((prev) => ({
      ...prev,
      management_goals: prev.management_goals.filter((_, i) => i !== index),
    }))
  }

  // SMART Goals handlers
  const updateSmartGoal = (index: number, value: string) => {
    setEditData((prev) => ({
      ...prev,
      smart_goals: prev.smart_goals.map((goal, i) =>
        i === index ? { ...goal, goal: value } : goal
      ),
    }))
  }

  const addSmartGoal = () => {
    setEditData((prev) => ({
      ...prev,
      smart_goals: [{ goal: "" }, ...prev.smart_goals],
    }))
  }

  const removeSmartGoal = (index: number) => {
    setEditData((prev) => ({
      ...prev,
      smart_goals: prev.smart_goals.filter((_, i) => i !== index),
    }))
  }

  // Interventions handlers
  const updateIntervention = (index: number, field: keyof Intervention, value: string) => {
    setEditData((prev) => ({
      ...prev,
      interventions_and_referrals: prev.interventions_and_referrals.map((intervention, i) =>
        i === index ? { ...intervention, [field]: value } : intervention
      ),
    }))
  }

  const addIntervention = () => {
    setEditData((prev) => ({
      ...prev,
      interventions_and_referrals: [
        { item: "", owner: "", target_date: "" },
        ...prev.interventions_and_referrals,
      ],
    }))
  }

  const removeIntervention = (index: number) => {
    setEditData((prev) => ({
      ...prev,
      interventions_and_referrals: prev.interventions_and_referrals.filter((_, i) => i !== index),
    }))
  }

  // Medications handlers
  const updateMedication = (index: number, field: keyof Medication, value: string | null) => {
    setEditData((prev) => ({
      ...prev,
      medications: prev.medications.map((med, i) =>
        i === index ? { ...med, [field]: value } : med
      ),
    }))
  }

  const addMedication = () => {
    setEditData((prev) => ({
      ...prev,
      medications: [{ medication_dosage_description: "", start_date: null, prescription_date: null, end_date: null }, ...prev.medications],
    }))
  }

  const removeMedication = (index: number) => {
    setEditData((prev) => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index),
    }))
  }

  // Care Gaps handlers
  const updateCareGap = (index: number, field: keyof CareGap, value: string | null) => {
    setEditData((prev) => ({
      ...prev,
      care_gaps: prev.care_gaps.map((gap, i) =>
        i === index ? { ...gap, [field]: value } : gap
      ),
    }))
  }

  const addCareGap = () => {
    setEditData((prev) => ({
      ...prev,
      care_gaps: [{ gap: "", due_date: null, plan: "" }, ...prev.care_gaps],
    }))
  }

  const removeCareGap = (index: number) => {
    setEditData((prev) => ({
      ...prev,
      care_gaps: prev.care_gaps.filter((_, i) => i !== index),
    }))
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    )
  }

  if (!carePlan) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <p className="text-muted-foreground">No care plan available</p>
        </CardContent>
      </Card>
    )
  }

  const getStatusBadge = (status: string) => {
    if (status === "approved") {
      return (
        <Badge className="bg-success/10 text-success hover:bg-success/10">
          <CheckCircle className="h-3 w-3 mr-1" />
          Approved
        </Badge>
      )
    }
    return (
      <Badge className="bg-muted text-foreground hover:bg-muted">
        Draft
      </Badge>
    )
  }

  // Extract goals and interventions from care plan details
  // planData is already defined above for patient info
  const managementGoals = planData.management_goals || []
  const smartGoals = planData.smart_goals || []
  const interventions = planData.interventions_and_referrals || []
  const medications = planData.medications || []
  const careGapsFromPlan = planData.care_gaps || []
  const needsProviderReview = planData.needs_provider_review || []

  return (
    <div className="space-y-5">
      {/* Action Bar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-3 border-b">
        <div className="flex items-center gap-3">
          {getStatusBadge(carePlan.care_plan_status)}
          <span className="text-sm text-muted-foreground">
            {carePlan.care_plan_status === "approved"
              ? `Approved ${formatTimestamp(carePlan.updated_at, "MMM d, yyyy", "N/A")}`
              : `Updated ${formatTimestamp(carePlan.updated_at, "MMM d, yyyy", "N/A")}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelEditing} disabled={isSaving} className="h-8 text-xs">
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving} className="h-8 text-xs bg-primary hover:bg-primary-hover">
                <Save className="h-3.5 w-3.5 mr-1" /> {isSaving ? "Saving..." : "Save"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={handleStartEditing} className="h-8 text-xs">
                <Edit className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyToEhr}
                disabled={copyState === "copying"}
                className="h-8 text-xs"
                title="Copy the whole care plan, formatted, to paste into an EHR encounter note"
              >
                {copyState === "copied" ? (
                  <><Check className="h-3.5 w-3.5 mr-1" /> Copied</>
                ) : (
                  <><ClipboardCopy className="h-3.5 w-3.5 mr-1" /> {copyState === "copying" ? "Copying..." : "Copy for EHR"}</>
                )}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handlePrint()} className="h-8 text-xs">
                <FileText className="h-3.5 w-3.5 mr-1" /> Print PDF
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Approval Actions (for physicians) */}
      {canApprove && !isEditing && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              {carePlan.care_plan_status === "approved"
                ? "This care plan has been approved."
                : "This care plan is awaiting approval."}
            </span>
            <div className="flex gap-2">
              {carePlan.care_plan_status === "approved" ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onReject}
                >
                  Unapprove
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={onApprove}
                  className="bg-success hover:bg-success"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Printable Content */}
      <style>{printStyles}</style>
      <div ref={printRef} className="print-container">
        {/* Screen View */}
        {isEditing ? (
          /* Edit Mode — keep accordion */
          <Accordion type="multiple" defaultValue={["management-goals", "medications", "smart-goals", "interventions", "care-gaps", "provider-review"]} className="space-y-4 no-print">
            {/* Management Goals Edit */}
            <AccordionItem value="management-goals" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    <span className="font-semibold">Management Goals</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addManagementGoal() }}>
                    <Plus className="h-4 w-4 mr-1" /> Add Goal
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3">
                  {editData.management_goals.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No management goals. Click "Add Goal" to create one.</p>
                  ) : (
                    editData.management_goals.map((goal, index) => (
                      <div key={index} className="border-l-2 border-info/30 pl-3 py-2">
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <input
                            type="text"
                            value={goal.condition}
                            onChange={(e) => updateManagementGoal(index, "condition", e.target.value)}
                            placeholder="Condition (e.g., HTN)"
                            className="font-medium text-sm bg-transparent border-none outline-none focus:ring-0 p-0 w-full placeholder:text-muted-foreground"
                          />
                          <Button variant="ghost" size="sm" onClick={() => removeManagementGoal(index)} className="text-destructive hover:text-destructive hover:bg-destructive/10 h-6 w-6 p-0 flex-shrink-0">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <Input value={goal.goal} onChange={(e) => updateManagementGoal(index, "goal", e.target.value)} placeholder="Goal (e.g., BP goal < 140/90 mm Hg)" className="text-sm" />
                      </div>
                    ))
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Medications Edit */}
            <AccordionItem value="medications" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Pill className="h-5 w-5" />
                    <span className="font-semibold">Medications ({editData.medications.length})</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addMedication() }}>
                    <Plus className="h-4 w-4 mr-1" /> Add Medication
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="overflow-x-auto">
                  {editData.medications.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No medications. Click "Add Medication" to create one.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium">Medication</th>
                          <th className="text-left py-2 font-medium">Start Date</th>
                          <th className="text-left py-2 font-medium">Rx Date</th>
                          <th className="text-left py-2 font-medium">End Date</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {editData.medications.map((med, index) => (
                          <tr key={index} className="border-b last:border-0">
                            <td className="py-2 pr-2">
                              <Input value={med.medication_dosage_description} onChange={(e) => updateMedication(index, "medication_dosage_description", e.target.value)} placeholder="Medication name and dosage..." className="text-sm" />
                            </td>
                            <td className="py-2 pr-2">
                              <Input type="date" value={med.start_date || ""} onChange={(e) => updateMedication(index, "start_date", e.target.value || null)} className="text-sm" />
                            </td>
                            <td className="py-2 pr-2">
                              <Input type="date" value={med.prescription_date || ""} onChange={(e) => updateMedication(index, "prescription_date", e.target.value || null)} className="text-sm" />
                            </td>
                            <td className="py-2 pr-2">
                              <Input type="date" value={med.end_date || ""} onChange={(e) => updateMedication(index, "end_date", e.target.value || null)} className="text-sm" />
                            </td>
                            <td className="py-2">
                              <Button variant="ghost" size="sm" onClick={() => removeMedication(index)} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                                <X className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* SMART Goals Edit */}
            <AccordionItem value="smart-goals" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    <span className="font-semibold">SMART Goals</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addSmartGoal() }}>
                    <Plus className="h-4 w-4 mr-1" /> Add Goal
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {editData.smart_goals.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No SMART goals. Click "Add Goal" to create one.</p>
                  ) : (
                    editData.smart_goals.map((goal, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground w-6">{index + 1}.</span>
                        <Input value={goal.goal} onChange={(e) => updateSmartGoal(index, e.target.value)} placeholder="Enter SMART goal..." className="text-sm flex-1" />
                        <Button variant="ghost" size="sm" onClick={() => removeSmartGoal(index)} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Interventions Edit */}
            <AccordionItem value="interventions" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    <span className="font-semibold">Interventions & Referrals</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addIntervention() }}>
                    <Plus className="h-4 w-4 mr-1" /> Add Intervention
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="overflow-x-auto">
                  {editData.interventions_and_referrals.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No interventions. Click "Add Intervention" to create one.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium">Item</th>
                          <th className="text-left py-2 font-medium">Owner</th>
                          <th className="text-left py-2 font-medium">Target Date</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {editData.interventions_and_referrals.map((intervention, index) => (
                          <tr key={index} className="border-b last:border-0">
                            <td className="py-2 pr-2"><Input value={intervention.item} onChange={(e) => updateIntervention(index, "item", e.target.value)} placeholder="Intervention item..." className="text-sm" /></td>
                            <td className="py-2 pr-2"><Input value={intervention.owner} onChange={(e) => updateIntervention(index, "owner", e.target.value)} placeholder="Owner..." className="text-sm" /></td>
                            <td className="py-2 pr-2"><Input value={intervention.target_date} onChange={(e) => updateIntervention(index, "target_date", e.target.value)} placeholder="Target date..." className="text-sm" /></td>
                            <td className="py-2"><Button variant="ghost" size="sm" onClick={() => removeIntervention(index)} className="text-destructive hover:text-destructive hover:bg-destructive/10"><X className="h-4 w-4" /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Care Gaps Edit */}
            <AccordionItem value="care-gaps" className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    <span className="font-semibold">Care Gaps ({editData.care_gaps.length})</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addCareGap() }}>
                    <Plus className="h-4 w-4 mr-1" /> Add Care Gap
                  </Button>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3">
                  {editData.care_gaps.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No care gaps. Click "Add Care Gap" to create one.</p>
                  ) : (
                    editData.care_gaps.map((gap, index) => (
                      <div key={index} className="border-l-2 border-warning/30 pl-3 py-2">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <Input value={gap.gap} onChange={(e) => updateCareGap(index, "gap", e.target.value)} placeholder="Care gap description..." className="text-sm font-medium" />
                          <Button variant="ghost" size="sm" onClick={() => removeCareGap(index)} className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0 flex-shrink-0"><X className="h-4 w-4" /></Button>
                        </div>
                        <Input value={gap.plan} onChange={(e) => updateCareGap(index, "plan", e.target.value)} placeholder="Plan to address this gap..." className="text-sm mb-2" />
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Due:</span>
                          <Input type="date" value={gap.due_date || ""} onChange={(e) => updateCareGap(index, "due_date", e.target.value || null)} className="text-sm w-40" />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Provider Review (read-only even in edit mode) */}
            {needsProviderReview.length > 0 && (
              <AccordionItem value="provider-review" className="border border-warning/30 rounded-lg px-4 bg-warning/10">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2 text-warning">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-semibold">Needs Provider Review ({needsProviderReview.length})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2">
                    {needsProviderReview.map((item: any, index: number) => (
                      <div key={index} className="text-sm flex items-start gap-2">
                        <span className="text-warning mt-0.5">•</span>
                        <span>{item.item}</span>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        ) : (
          /* View Mode — document-style layout */
          <div className="space-y-6 no-print">
            {/* Patient Header */}
            <div className="bg-card shadow-sm rounded-lg p-5">
              <h2 className="text-lg font-bold mb-3">Patient Care Plan</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Name:</span>{" "}
                  <span className="font-medium">{lastName}, {firstName}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">DOB:</span>{" "}
                  <span className="font-medium">{formatDobDisplay(dob)}</span>
                  {ageFromDob(dob) !== null && (
                    <span className="text-muted-foreground ml-1">(Age: {ageFromDob(dob)})</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">MRN:</span>{" "}
                  <span className="font-medium">{patientInfo.external_id || "N/A"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Phone:</span>{" "}
                  <span className="font-medium">{patientInfo.phone_number || "N/A"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Provider:</span>{" "}
                  <span className="font-medium">{planData.provider_name || "N/A"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Type:</span>{" "}
                  <span className="font-medium">{carePlan.care_plan_type}</span>
                </div>
              </div>
            </div>

            {/* Management Goals */}
            <div>
              <div className="bg-primary text-white px-4 py-2.5 rounded-t-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                <Target className="h-4 w-4" /> Management Goals
              </div>
              <div className="bg-card shadow-sm rounded-b-lg p-4">
                {managementGoals.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No management goals defined</p>
                ) : (
                  <div className="space-y-3">
                    {managementGoals.map((goal: any, index: number) => (
                      <div key={index} className="border-l-3 border-primary/30 pl-3 py-1">
                        <div className="font-medium text-sm">{goal.condition}</div>
                        <div className="text-sm text-muted-foreground">{goal.goal}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Medications */}
            {medications.length > 0 && (
              <div>
                <div className="bg-primary text-white px-4 py-2.5 rounded-t-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                  <Pill className="h-4 w-4" /> Medications ({medications.length})
                </div>
                <div className="bg-card shadow-sm rounded-b-lg p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-2 px-2 font-medium">Medication</th>
                          <th className="text-left py-2 px-2 font-medium">Start Date</th>
                          <th className="text-left py-2 px-2 font-medium">Rx Date</th>
                          <th className="text-left py-2 px-2 font-medium">End Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {medications.map((med: any, index: number) => (
                          <tr key={index} className="border-b last:border-0">
                            <td className="py-2 px-2">{med.medication_dosage_description}</td>
                            <td className="py-2 px-2 text-muted-foreground">{safeFormatDate(med.start_date, "MMM d, yyyy", "-")}</td>
                            <td className="py-2 px-2 text-muted-foreground">{safeFormatDate(med.prescription_date, "MMM d, yyyy", "-")}</td>
                            <td className="py-2 px-2 text-muted-foreground">{safeFormatDate(med.end_date, "MMM d, yyyy", "-")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* SMART Goals */}
            {smartGoals.length > 0 && (
              <div>
                <div className="bg-primary text-white px-4 py-2.5 rounded-t-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                  <Target className="h-4 w-4" /> SMART Goals
                </div>
                <div className="bg-card shadow-sm rounded-b-lg p-4">
                  <div className="space-y-2">
                    {smartGoals.map((goal: any, index: number) => (
                      <div key={index} className="text-sm">
                        {index + 1}. {goal.goal}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Care Gaps */}
            {careGapsFromPlan.length > 0 && (
              <div>
                <div className="bg-primary text-white px-4 py-2.5 rounded-t-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                  <Clock className="h-4 w-4" /> Care Gaps ({careGapsFromPlan.length})
                </div>
                <div className="bg-card shadow-sm rounded-b-lg p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-2 px-2 font-medium">Gap</th>
                          <th className="text-left py-2 px-2 font-medium">Plan</th>
                          <th className="text-left py-2 px-2 font-medium w-28">Due Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {careGapsFromPlan.map((gap: any, index: number) => (
                          <tr key={index} className="border-b last:border-0">
                            <td className="py-2 px-2 font-medium">{gap.gap}</td>
                            <td className="py-2 px-2 text-muted-foreground">{gap.plan || "-"}</td>
                            <td className="py-2 px-2 text-muted-foreground">{safeFormatDate(gap.due_date, "MMM d, yyyy", "-")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Interventions & Referrals */}
            <div>
              <div className="bg-primary text-white px-4 py-2.5 rounded-t-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                <Activity className="h-4 w-4" /> Interventions & Referrals
              </div>
              <div className="bg-card shadow-sm rounded-b-lg p-4">
                {interventions.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No interventions defined</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-2 px-2 font-medium">Item</th>
                          <th className="text-left py-2 px-2 font-medium">Owner</th>
                          <th className="text-left py-2 px-2 font-medium">Target Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {interventions.map((intervention: any, index: number) => (
                          <tr key={index} className="border-b last:border-0">
                            <td className="py-2 px-2">{intervention.item}</td>
                            <td className="py-2 px-2 text-muted-foreground">{intervention.owner}</td>
                            <td className="py-2 px-2 text-muted-foreground">{intervention.target_date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Needs Provider Review */}
            {needsProviderReview.length > 0 && (
              <div>
                <div className="bg-warning text-white px-4 py-2.5 rounded-t-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                  <AlertTriangle className="h-4 w-4" /> Items Requiring Provider Review ({needsProviderReview.length})
                </div>
                <div className="bg-warning/10 shadow-sm rounded-b-lg p-4">
                  <div className="space-y-2">
                    {needsProviderReview.map((item: any, index: number) => (
                      <div key={index} className="text-sm flex items-start gap-2">
                        <span className="text-warning mt-0.5">•</span>
                        <span>{item.item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Print-only Dense View */}
        <div className="print-only text-xs space-y-2">
          {/* Patient Header */}
          <div className="border rounded p-2 bg-muted/50">
            <h1 className="text-lg font-bold mb-1">Patient Care Plan</h1>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
              <div>
                <span className="font-semibold">Name:</span> {lastName}, {firstName}
              </div>
              <div>
                <span className="font-semibold">DOB:</span> {formatDobDisplay(dob)}{" "}
                {ageFromDob(dob) !== null && `(Age: ${ageFromDob(dob)})`}
              </div>
              <div>
                <span className="font-semibold">MRN:</span> {patientInfo.external_id || "N/A"}
              </div>
              <div>
                <span className="font-semibold">Phone:</span> {patientInfo.phone_number || "N/A"}
              </div>
              <div>
                <span className="font-semibold">Address:</span> {patientInfo.address || "N/A"}
              </div>
              <div>
                <span className="font-semibold">Provider:</span> {planData.provider_name || "N/A"}
              </div>
              <div className="col-span-2 text-right text-[10px] text-muted-foreground mt-1">
                Generated: {format(new Date(), "MMMM d, yyyy")}
              </div>
            </div>
          </div>

          {/* Management Goals */}
          <div>
            <h2 className="text-sm font-bold mb-1 bg-border px-2 py-0.5">
              Detailed Management Goals by Condition
            </h2>
            <div className="space-y-0.5">
              {managementGoals.map((goal: any, idx: number) => (
                <div key={idx} className="border-l-2 border-info/30 pl-2 py-0.5">
                  <div className="font-semibold text-[11px]">{goal.condition}</div>
                  <div className="text-[10px] text-foreground/80">{goal.goal}</div>
                </div>
              ))}
            </div>
          </div>

          {/* SMART Goals */}
          <div>
            <h2 className="text-sm font-bold mb-1 bg-border px-2 py-0.5">
              SMART Goals
            </h2>
            <div className="space-y-0.5">
              {smartGoals.map((goal: any, idx: number) => (
                <div key={idx} className="pl-2 text-[11px]">
                  {idx + 1}. {goal.goal}
                </div>
              ))}
            </div>
          </div>

          {/* Care Gaps */}
          <div>
            <h2 className="text-sm font-bold mb-1 bg-border px-2 py-0.5">
              Care Gaps & Preventive Care
            </h2>
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="border px-1 py-0.5 text-left">Gap</th>
                  <th className="border px-1 py-0.5 text-left w-20">Due Date</th>
                  <th className="border px-1 py-0.5 text-left">Plan</th>
                </tr>
              </thead>
              <tbody>
                {careGapsFromPlan.length > 0 ? (
                  careGapsFromPlan.map((gap: any, idx: number) => (
                    <tr key={idx}>
                      <td className="border px-1 py-0.5">{gap.gap}</td>
                      <td className="border px-1 py-0.5">
                        {safeFormatDate(gap.due_date, "M/d/yyyy")}
                      </td>
                      <td className="border px-1 py-0.5">{gap.plan}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="border px-1 py-0.5 text-center italic">
                      No care gaps
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Medications */}
          <div>
            <h2 className="text-sm font-bold mb-1 bg-border px-2 py-0.5">
              Current Medications
            </h2>
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="border px-1 py-0.5 text-left">Medication</th>
                  <th className="border px-1 py-0.5 text-left w-16">Start</th>
                  <th className="border px-1 py-0.5 text-left w-16">Rx Date</th>
                  <th className="border px-1 py-0.5 text-left w-16">End</th>
                </tr>
              </thead>
              <tbody>
                {medications.length > 0 ? (
                  medications.map((med: any, idx: number) => (
                    <tr key={idx}>
                      <td className="border px-1 py-0.5">{med.medication_dosage_description}</td>
                      <td className="border px-1 py-0.5">{safeFormatDate(med.start_date, "M/d/yy", "-")}</td>
                      <td className="border px-1 py-0.5">{safeFormatDate(med.prescription_date, "M/d/yy", "-")}</td>
                      <td className="border px-1 py-0.5">{safeFormatDate(med.end_date, "M/d/yy", "-")}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="border px-1 py-0.5 text-center italic">No medications</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Interventions */}
          <div>
            <h2 className="text-sm font-bold mb-1 bg-border px-2 py-0.5">
              Interventions & Referrals
            </h2>
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="border px-1 py-0.5 text-left">Item</th>
                  <th className="border px-1 py-0.5 text-left">Owner</th>
                  <th className="border px-1 py-0.5 text-left">Target Date</th>
                </tr>
              </thead>
              <tbody>
                {interventions.length > 0 ? (
                  interventions.map((item: any, idx: number) => (
                    <tr key={idx}>
                      <td className="border px-1 py-0.5">{item.item}</td>
                      <td className="border px-1 py-0.5">{item.owner}</td>
                      <td className="border px-1 py-0.5">{item.target_date}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="border px-1 py-0.5 text-center italic">
                      No interventions
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Needs Provider Review */}
          {needsProviderReview.length > 0 && (
            <div>
              <h2 className="text-sm font-bold mb-1 bg-warning/25 px-2 py-0.5">
                ⚠️ Needs Provider Review
              </h2>
              <ul className="list-disc list-inside text-[11px]">
                {needsProviderReview.map((item: any, idx: number) => (
                  <li key={idx}>{item.item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PatientCarePlanTab
